import { useState } from "react";
import { FiFileText } from "react-icons/fi";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { SsnField, EinField, PhoneField } from "@/components/common/masked-form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  portalDependents,
  portalEmployers,
  portalOtherBusinesses,
  portalPaystubs,
  type PortalCollectionApi,
} from "@/features/portal/api";
import { dollarsToCents } from "@/features/worksheets/money";
import { ENTITY_TYPE_OPTIONS, PAY_PERIOD_OPTIONS } from "@/features/worksheets/types";
import {
  CollectionCard,
  type Column,
  type PortalRecord,
  usePortalCollection,
} from "./collection-card";
import { PortalCrudDialog } from "./crud-dialog";
import { CheckboxInput, MoneyInput, SelectInput } from "./fields";
import { bool, dash, moneyDecimal, num, str } from "./record";

const nonNegMoney = Yup.string().test(
  "non-negative",
  "Must be zero or more",
  (v) => !v || dollarsToCents(v) >= 0,
);

// --- Dependents --------------------------------------------------------------

interface DependentValues {
  name: string;
  ssn: string;
  age: string;
  relationship: string;
  claimed_on_return: boolean;
  contributes_to_household: boolean;
}

function DependentDialog({
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const initial: DependentValues = {
    name: str(record ?? {}, "name"),
    ssn: str(record ?? {}, "ssn"),
    age: num(record ?? {}, "age")?.toString() ?? "",
    relationship: str(record ?? {}, "relationship"),
    claimed_on_return: bool(record ?? {}, "claimed_on_return"),
    contributes_to_household: bool(record ?? {}, "contributes_to_household"),
  };

  async function onSave(v: DependentValues) {
    const payload = {
      name: v.name,
      ssn: v.ssn,
      age: v.age === "" ? null : Number(v.age),
      relationship: v.relationship,
      claimed_on_return: v.claimed_on_return,
      contributes_to_household: v.contributes_to_household,
    };
    if (editing && record) await portalDependents.update(record.id, payload);
    else await portalDependents.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Dependent"
      initialValues={initial}
      validationSchema={Yup.object({
        name: Yup.string().trim().required("Required"),
        age: Yup.number().typeError("Enter a number").min(0, "Must be 0 or more").nullable(),
      })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="name" label="Name" />
      <div className="grid grid-cols-2 gap-4">
        <SsnField name="ssn" label="SSN" />
        <FormField name="age" label="Age" inputMode="numeric" />
      </div>
      <FormField name="relationship" label="Relationship" />
      <CheckboxInput name="claimed_on_return" label="Claimed on the tax return" />
      <CheckboxInput name="contributes_to_household" label="Contributes to the household" />
    </PortalCrudDialog>
  );
}

function DependentsSection() {
  const { rows, loading, reload } = usePortalCollection(portalDependents as PortalCollectionApi<PortalRecord>);
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });

  const columns: Column<PortalRecord>[] = [
    { header: "Name", cell: (r) => dash(str(r, "name")) },
    { header: "SSN", cell: (r) => dash(str(r, "ssn_label")) },
    { header: "Age", cell: (r) => num(r, "age") ?? "—" },
    { header: "Relationship", cell: (r) => dash(str(r, "relationship")) },
  ];

  return (
    <>
      <CollectionCard
        title="Dependents"
        addLabel="Add Dependent"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => portalDependents.remove(row.id)}
        onDeleted={reload}
      />
      <DependentDialog
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

// --- Employers (+ pay stubs) -------------------------------------------------

interface EmployerValues {
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  occupation: string;
  ein: string;
  pay_period: string;
  gross_wages: string;
  federal_tax: string;
  state_tax: string;
  local_tax: string;
  no_fica: boolean;
  no_medicare: boolean;
}

function EmployerDialog({
  belongsTo,
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  belongsTo: "TAXPAYER" | "SPOUSE";
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const r = record ?? {};
  const initial: EmployerValues = {
    name: str(r, "name"),
    street: str(r, "street"),
    city: str(r, "city"),
    state: str(r, "state"),
    zip: str(r, "zip"),
    phone: str(r, "phone"),
    occupation: str(r, "occupation"),
    ein: str(r, "ein"),
    pay_period: str(r, "pay_period"),
    gross_wages: str(r, "gross_wages"),
    federal_tax: str(r, "federal_tax"),
    state_tax: str(r, "state_tax"),
    local_tax: str(r, "local_tax"),
    no_fica: bool(r, "no_fica"),
    no_medicare: bool(r, "no_medicare"),
  };

  async function onSave(v: EmployerValues) {
    const payload = { ...v, belongs_to: belongsTo };
    if (editing && record) await portalEmployers.update(record.id, payload);
    else await portalEmployers.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Employer"
      className="sm:max-w-2xl"
      initialValues={initial}
      validationSchema={Yup.object({ name: Yup.string().trim().required("Required") })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="name" label="Employer name" />
      <div className="grid grid-cols-2 gap-4">
        <FormField name="occupation" label="Occupation" />
        <EinField name="ein" label="EIN" />
      </div>
      <FormField name="street" label="Street" />
      <div className="grid grid-cols-3 gap-4">
        <FormField name="city" label="City" />
        <FormField name="state" label="State" />
        <FormField name="zip" label="ZIP" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <PhoneField name="phone" label="Phone" />
        <SelectInput name="pay_period" label="Pay period" options={PAY_PERIOD_OPTIONS} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField name="gross_wages" label="Gross wages" inputMode="decimal" placeholder="0.00" />
        <FormField name="federal_tax" label="Federal tax" inputMode="decimal" placeholder="0.00" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField name="state_tax" label="State tax" inputMode="decimal" placeholder="0.00" />
        <FormField name="local_tax" label="Local tax" inputMode="decimal" placeholder="0.00" />
      </div>
      <div className="flex gap-6">
        <CheckboxInput name="no_fica" label="No FICA withheld" />
        <CheckboxInput name="no_medicare" label="No Medicare withheld" />
      </div>
    </PortalCrudDialog>
  );
}

// --- Pay stubs (per employer) ------------------------------------------------

interface PayStubValues {
  item: string;
  category: string;
  amount: string;
}

function PayStubsDialog({
  employer,
  open,
  onOpenChange,
}: {
  employer: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const api = employer ? portalPaystubs(employer.id) : null;
  const { rows, loading, reload } = usePortalCollection(
    (api ?? { list: async () => [], create: async () => ({ id: "" }), update: async () => ({ id: "" }), remove: async () => {} }) as PortalCollectionApi<PortalRecord>,
    employer?.id, // re-query when the employer changes
  );
  const [sub, setSub] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });

  const columns: Column<PortalRecord>[] = [
    { header: "Item", cell: (r) => dash(str(r, "item")) },
    { header: "Category", cell: (r) => dash(str(r, "category")) },
    { header: "Amount", cell: (r) => moneyDecimal(r, "amount") },
  ];

  return (
    <>
      <PortalCrudDialogShell open={open} onOpenChange={onOpenChange} title="Pay stubs">
        {api && (
          <CollectionCard
            title="Pay stub lines"
            addLabel="Add line"
            columns={columns}
            rows={rows}
            loading={loading}
            onAdd={() => setSub({ open: true, record: null })}
            onEdit={(record) => setSub({ open: true, record })}
            onDelete={(row) => api.remove(row.id)}
        onDeleted={reload}
          />
        )}
      </PortalCrudDialogShell>

      {api && (
        <PayStubLineDialog
          api={api}
          record={sub.record}
          open={sub.open}
          onOpenChange={(o) => setSub((s) => ({ ...s, open: o }))}
          onSaved={reload}
        />
      )}
    </>
  );
}

function PayStubLineDialog({
  api,
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  api: PortalCollectionApi;
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const initial: PayStubValues = {
    item: str(record ?? {}, "item"),
    category: str(record ?? {}, "category"),
    // PayStub.amount is a plain DecimalField (dollars string), not cents.
    amount: str(record ?? {}, "amount"),
  };

  async function onSave(v: PayStubValues) {
    const payload = { item: v.item, category: v.category, amount: v.amount };
    if (editing && record) await api.update(record.id, payload);
    else await api.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Pay stub line"
      initialValues={initial}
      validationSchema={Yup.object({
        item: Yup.string().trim().required("Required"),
        amount: nonNegMoney,
      })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="item" label="Item" />
      <FormField name="category" label="Category" />
      <MoneyInput name="amount" label="Amount" />
    </PortalCrudDialog>
  );
}

/** A plain (non-Formik) dialog used to host the nested pay-stubs collection. */
function PortalCrudDialogShell({
  open,
  onOpenChange,
  title,
  children,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

function EmployersSection({
  belongsTo,
  title,
}: {
  belongsTo: "TAXPAYER" | "SPOUSE";
  title: string;
}) {
  const { rows, loading, reload } = usePortalCollection(
    portalEmployers as PortalCollectionApi<PortalRecord>,
    `belongs_to=${belongsTo}`,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });
  const [payStubs, setPayStubs] = useState<{ open: boolean; employer: PortalRecord | null }>({
    open: false,
    employer: null,
  });

  const columns: Column<PortalRecord>[] = [
    { header: "Employer", cell: (r) => dash(str(r, "name")) },
    { header: "Occupation", cell: (r) => dash(str(r, "occupation")) },
    { header: "Pay period", cell: (r) => dash(str(r, "pay_period")) },
    { header: "Gross wages", cell: (r) => dash(str(r, "gross_wages") ? `$${str(r, "gross_wages")}` : "") },
    {
      header: "",
      cell: (r) => (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => setPayStubs({ open: true, employer: r })}
        >
          <FiFileText className="size-4" /> Pay stubs
        </Button>
      ),
    },
  ];

  return (
    <>
      <CollectionCard
        title={title}
        addLabel="Add Employer"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => portalEmployers.remove(row.id)}
        onDeleted={reload}
      />
      <EmployerDialog
        belongsTo={belongsTo}
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
      <PayStubsDialog
        employer={payStubs.employer}
        open={payStubs.open}
        onOpenChange={(open) => setPayStubs((s) => ({ ...s, open }))}
      />
    </>
  );
}

// --- Other business interests ------------------------------------------------

interface OtherBusinessValues {
  name: string;
  percent: string;
  title: string;
  phone: string;
  ein: string;
  entity_type: string;
  address: string;
}

function OtherBusinessDialog({
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  record: PortalRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  const editing = !!record;
  const r = record ?? {};
  const initial: OtherBusinessValues = {
    name: str(r, "name"),
    percent: str(r, "percent"),
    title: str(r, "title"),
    phone: str(r, "phone"),
    ein: str(r, "ein"),
    entity_type: str(r, "entity_type"),
    address: str(r, "address"),
  };

  async function onSave(v: OtherBusinessValues) {
    const payload = { ...v };
    if (editing && record) await portalOtherBusinesses.update(record.id, payload);
    else await portalOtherBusinesses.create(payload);
  }

  return (
    <PortalCrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={editing}
      noun="Business"
      initialValues={initial}
      validationSchema={Yup.object({ name: Yup.string().trim().required("Required") })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="name" label="Business name" />
      <div className="grid grid-cols-2 gap-4">
        <FormField name="title" label="Your title" />
        <FormField name="percent" label="Ownership %" inputMode="decimal" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <PhoneField name="phone" label="Phone" />
        <EinField name="ein" label="EIN" />
      </div>
      <SelectInput name="entity_type" label="Entity type" options={ENTITY_TYPE_OPTIONS} />
      <FormField name="address" label="Address" />
    </PortalCrudDialog>
  );
}

function OtherBusinessSection() {
  const { rows, loading, reload } = usePortalCollection(
    portalOtherBusinesses as PortalCollectionApi<PortalRecord>,
  );
  const [dialog, setDialog] = useState<{ open: boolean; record: PortalRecord | null }>({
    open: false,
    record: null,
  });

  const columns: Column<PortalRecord>[] = [
    { header: "Business", cell: (r) => dash(str(r, "name")) },
    { header: "Ownership %", cell: (r) => (str(r, "percent") ? `${str(r, "percent")}%` : "—") },
    { header: "Title", cell: (r) => dash(str(r, "title")) },
    { header: "EIN", cell: (r) => dash(str(r, "ein_label")) },
  ];

  return (
    <>
      <CollectionCard
        title="Other business interests"
        addLabel="Add Business"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => setDialog({ open: true, record: null })}
        onEdit={(record) => setDialog({ open: true, record })}
        onDelete={(row) => portalOtherBusinesses.remove(row.id)}
        onDeleted={reload}
      />
      <OtherBusinessDialog
        record={dialog.record}
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        onSaved={reload}
      />
    </>
  );
}

// --- Tab ---------------------------------------------------------------------

export function PersonalTab() {
  return (
    <div className="grid gap-6">
      <DependentsSection />
      <EmployersSection belongsTo="TAXPAYER" title="Taxpayer employers" />
      <EmployersSection belongsTo="SPOUSE" title="Spouse employers" />
      <OtherBusinessSection />
    </div>
  );
}
